import FastImage from '@d11/react-native-fast-image';
import React, { useMemo } from 'react';
import { Image, Share, StyleSheet, TouchableOpacity, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { PlaybackSpeed } from './PlaybackSpeed';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { COLORS, IS_ANDROID, isTablet, setOpacity } from '../../constants';
import {
  selectAppConfig,
  selectDivRadius,
} from '../../features/appconfig/store/selectors';
import { selectCurrentEpisode } from '../../features/currentpodcast/store/selectors';
import { showPlayerModal } from '../../features/currentpodcast/store/slice';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { useShadowStyles } from '../../hooks/useShadowStyles';
import { formatEpisodeDate } from '../../localization/datetime';
import i18n from '../../localization/i18n';
import PodcastPlayerModalScreen from '../../screens/Podcast/PodcastPlayerModalScreen';
import { CustomIcon } from '../CustomIcon';
import CustomText from '../CustomText';
import { PlayerService } from '../Player/PlayerService';
import { PlayPauseButton } from '../PlayPauseButton';
import { SleepTimer } from '../SleepTimer';

export const MiniPlayerCard: React.FC = () => {
  const divRadius = useAppSelector(selectDivRadius);
  const currentPodcastEpisode = useAppSelector(selectCurrentEpisode);
  const dispatch = useAppDispatch();
  const appConfig = useAppSelector(selectAppConfig);
  const shadowStyles = useShadowStyles();

  const episodeTitleFontSize = useAdjustedFontSize(mvs(14));
  const dateFontSize = useAdjustedFontSize(mvs(12));

  const shareIcon = useMemo(() => {
    const message =
      currentPodcastEpisode.title + ' (' + currentPodcastEpisode.podcastTitle + ')';
    return (
      <CustomIcon
        name="share_player"
        color={setOpacity('#252525', 70)}
        size={mvs(20)}
        onPress={() => {
          Share.share({
            message:
              i18n.t('share.podcast', {
                podcast: message,
                appName: appConfig.nomRadio,
              }) + (IS_ANDROID ? '\n' + currentPodcastEpisode.url : ''),
            url: currentPodcastEpisode.url,
          });
        }}
      />
    );
  }, [appConfig, currentPodcastEpisode]);

  if (!currentPodcastEpisode) {
    return;
  }

  return (
    <TouchableOpacity
      style={styles.container}
      onPress={() => dispatch(showPlayerModal())}
      activeOpacity={0.95}
    >
      <View style={[styles.card, { borderRadius: mvs(divRadius) }, shadowStyles]}>
        {/* podcast thumbnail */}
        {/* thumbnail border radius is 70% of the main card radius for visual hierarchy */}
        <View style={[styles.thumbnailContainer, { borderRadius: mvs(divRadius) * 0.7 }]}>
          <FastImage
            source={{ uri: currentPodcastEpisode.image }}
            style={styles.thumbnail}
            resizeMode={FastImage.resizeMode.cover}
          />
        </View>

        {/* content section */}
        <View style={styles.contentContainer}>
          {/* text content */}
          <View style={styles.textContainer}>
            <CustomText
              style={[styles.episodeTitle, { fontSize: episodeTitleFontSize }]}
              weight="SemiBold"
              numberOfLines={1}
            >
              {currentPodcastEpisode.title}
            </CustomText>
            <CustomText
              style={[styles.date, { fontSize: dateFontSize }]}
              weight="Regular"
              numberOfLines={1}
            >
              {formatEpisodeDate(currentPodcastEpisode.date)}
            </CustomText>
          </View>

          {/* play button */}
          <View style={styles.buttonsContainer}>
            {isTablet && (
              <PlaybackSpeed textColorStyle={setOpacity('#252525', 70)} textSize={18} />
            )}
            {isTablet && shareIcon}
            {isTablet && (
              <SleepTimer
                size={mvs(30)}
                iconSize={mvs(20)}
                color={setOpacity('#252525', 70)}
                style={styles.sleepTimer}
              />
            )}

            <PlayPauseButton
              size={35}
              iconSize={17}
              podcastEpisode={currentPodcastEpisode}
              type="miniPlayer"
              style={styles.playButton}
            />

            {isTablet && (
              <TouchableOpacity
                style={styles.skipButton}
                onPress={() => PlayerService.skipForward(30)}
                activeOpacity={0.7}
              >
                <View style={styles.skipIconContainer}>
                  <Image
                    style={styles.skipIcon}
                    source={require('../../assets/icons/skip-forward-tablet.png')}
                    resizeMode="contain"
                  />
                  <CustomText style={styles.skipText} weight="SemiBold">
                    {30}
                  </CustomText>
                </View>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
      <PodcastPlayerModalScreen />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: mvs(74),
  },
  card: {
    backgroundColor: COLORS.WHITE,
    padding: mvs(8),
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
  },
  thumbnailContainer: {
    width: mvs(56),
    height: mvs(56),
    borderRadius: mvs(8),
    overflow: 'hidden',
    marginRight: mvs(12),
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
  },
  textContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  episodeTitle: {
    color: '#252525',
    lineHeight: mvs(18),
    marginBottom: mvs(2),
  },
  sleepTimer: {
    marginLeft: mvs(8),
  },
  playButton: {
    marginLeft: mvs(13),
    marginRight: isTablet ? mvs(17) : undefined,
  },
  date: {
    color: '#9c9c9c',
    lineHeight: mvs(16),
  },
  buttonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  skipText: {
    position: 'absolute',
    fontSize: mvs(8),
    color: '#252525',
    alignSelf: 'center',
    top: mvs(8.5),
    paddingLeft: mvs(1),
  },
  skipButton: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  skipIconContainer: {
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  skipIcon: {
    height: mvs(25),
    width: mvs(25),
  },
});

export default MiniPlayerCard;
